import React from 'react'
import * as Redux from 'react-redux'
import { styled } from 'next-yak'

import { t } from '@owl-nest/localize'
import * as model from '@owl-nest/models'
import { CATEGORY, EVENT_TYPE, useTracking } from '@owl-nest/shadow'

import * as plume from '@ulule/owl-kit-components/next'

import * as modalAction from '../../../../actions/modal'
import * as projectModel from '../../../models/project'
import { TRACKING_ACTION, TRACKING_SOURCE } from '../../../views/detail/utils/trackerEnums'
import * as modalReducer from '../../../../reducers/modals/postCampaignModal'
import { type RootState } from '../../../../reducers'

const Wrapper = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
`

// TODO: Discuss use of another Heading style to avoid overriding the `font-weight`
const Title = styled(plume.styles.heading.XXS)`
  font-weight: 700;
  margin-bottom: 10px;
`

const Text = styled(plume.styles.copy.S.regular)`
  margin-bottom: 15px;
`

const Button = styled(plume.styles.button.CallToAction)`
  background-color: ${plume.COLORS.SECONDARY_GREEN_500};
  margin-bottom: 10px;
  transition: 0.3s ease;

  &:hover {
    // HACK: Was not replaced with a proper replacement during legacy colors deletion (was #64D088 before that).
    //  If you're seeing weird results, please reach out to Product to see if we shouldn't reintroduce #64D088 as a legacy token.
    background-color: ${plume.COLORS.SECONDARY_GREEN_800};
  }
`

export const PostCampaignModal = (): React.ReactElement<unknown> => {
  const dispatch = Redux.useDispatch()

  const project = Redux.useSelector<RootState, projectModel.Project>((state) => state.application.project.data)
  const modal = Redux.useSelector<RootState, modalReducer.State>((state) => state.application.postCampaingModal)

  const tracking = useTracking()

  const postCampaignLink = project?.post_campaign_link
  const offboardingType = project?.onboarding_steps?.offboarding?.type
  const mainTag = model.project.mainTag<projectModel.ExtraFields>(project)

  const handleClosing = (): void => {
    dispatch(modalAction.toggleModalPostCampaign(false))
    dispatch(modalAction.setRewardId(''))
  }

  let previewTranslations = {
    button: t('Order'),
    text: t('Rest assured! We’re selling here 👇'),
    title: t('Did you miss the campaign?'),
  }
  if (offboardingType) {
    if (offboardingType === 'support') {
      previewTranslations = {
        button: t('Back the project'),
        text: t('Rest assured! Back the project here 👇'),
        title: t('Did you miss the campaign?'),
      }
    } else if (offboardingType === 'follow') {
      previewTranslations = {
        button: t('Follow the project'),
        text: t('Follow us here 👇'),
        title: t('Did you miss the campaign?'),
      }
    }
  }

  if (postCampaignLink === undefined) {
    return (
      <plume.Modal
        open={modal.open}
        closable
        onClose={handleClosing}
        buttons={{
          type: 'buttonAsLink',
          props: {
            href: project.main_tag?.absolute_url,
            children: t('All live projects'),
          },
        }}
      >
        <Wrapper>
          <Title>{t('Did you miss the campaign?')}</Title>
          {mainTag && (
            <Text>
              {t('Discover more %(mainTag)s live projects 👇', {
                mainTag,
              })}
            </Text>
          )}
        </Wrapper>
      </plume.Modal>
    )
  }

  return (
    <plume.Modal
      open={modal.open}
      closable
      onClose={handleClosing}
      buttons={{
        type: 'buttonAsLink',
        props: {
          href: postCampaignLink.url,
          onClick: track,
          children: previewTranslations.button,
        },
      }}
    >
      <Wrapper>
        <Title>{previewTranslations.title}</Title>
        <Text>{previewTranslations.text}</Text>
      </Wrapper>
    </plume.Modal>
  )

  function track() {
    if (offboardingType !== undefined) {
      tracking.track({
        event: EVENT_TYPE.GENERIC,
        eventCategory: CATEGORY.PROJECT_PAGE,
        eventAction: TRACKING_ACTION.CLICK_OFFBOARDING,
        eventLabel: TRACKING_SOURCE.MODAL,
        eventValue: offboardingType,
      })
    }
  }
}
